import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Add, Bin, ChevronDown, Search } from "react-ikonate";
import styles from "./VersionPicker.module.css";

/**
 * Version picker — a GitHub branch-selector-style dropdown that sits above the
 * product editor grid.
 *
 * A "version" is a NEW COPY of the product that inherits its metadata but can
 * carry its own name/description/tags/thumbnail/content. Exactly ONE version is
 * the active/live one at a time — marked with a green status dot. The others
 * are draft/scheduled states, not parallel live listings. Selecting a version
 * swaps the editor's content in and out; the parent owns the per-version
 * snapshots.
 *
 * A dropdown (instead of a tab bar) keeps the header uncluttered no matter how
 * many versions exist, and lets the user search/filter to find one quickly.
 */
const VersionPicker = ({
  versions,
  currentVersionId,
  onSelect,
  onAdd,
  onSetActive,
  onDelete,
  onDeleteProduct,
  productName,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const rootRef = useRef(null);

  const current =
    versions.find((v) => v.id === currentVersionId) || versions[0];
  const canDelete = versions.length > 1 && current && !current.active;
  const canDeleteProduct = typeof onDeleteProduct === "function";

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return versions;
    return versions.filter((v) => v.name.toLowerCase().includes(q));
  }, [versions, query]);

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
  };

  const handlePick = (id) => {
    onSelect(id);
    closeMenu();
  };

  const handleCreate = () => {
    onAdd();
    closeMenu();
  };

  const handleDeleteClick = () => {
    if (!canDelete && !canDeleteProduct) return;
    setConfirmingDelete(true);
  };

  const handleConfirmDelete = () => {
    setConfirmingDelete(false);
    onDelete(current.id);
  };

  const handleConfirmDeleteProduct = () => {
    setConfirmingDelete(false);
    onDeleteProduct();
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(false);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.bar}>
        <div className={styles.picker} ref={rootRef}>
          <button
            type="button"
            className={styles.trigger}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
            title="Switch between this product's versions. The green dot marks the live version."
          >
            <span
              className={`${styles.dot} ${
                current && current.active ? styles.dotLive : styles.dotDraft
              }`}
              aria-hidden="true"
            />
            <span className={styles.triggerName}>
              {current ? current.name : "—"}
            </span>
            <ChevronDown size={16} className={styles.triggerChevron} />
          </button>

          {open && (
            <div className={styles.menu} role="listbox">
              <div className={styles.searchRow}>
                <Search size={15} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a version…"
                  autoFocus
                />
              </div>

              <div className={styles.list}>
                {filtered.length === 0 && (
                  <div className={styles.empty}>No versions match.</div>
                )}
                {filtered.map((version) => {
                  const isCurrent = version.id === currentVersionId;
                  return (
                    <button
                      key={version.id}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      className={`${styles.option} ${
                        isCurrent ? styles.optionCurrent : ""
                      }`}
                      onClick={() => handlePick(version.id)}
                      title={`Load the "${version.name}" version into the editor.`}
                    >
                      <span
                        className={`${styles.dot} ${
                          version.active ? styles.dotLive : styles.dotDraft
                        }`}
                        aria-hidden="true"
                      />
                      <span className={styles.optionName}>{version.name}</span>
                      {version.active && (
                        <span className={styles.liveTag}>Live</span>
                      )}
                      {isCurrent && (
                        <span className={styles.check} aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className={styles.createRow}
                onClick={handleCreate}
                title="Create a new version that copies the current version's content, so you can edit it separately."
              >
                <Add size={15} />
                <span>New version (copy of current)</span>
              </button>
            </div>
          )}
        </div>

        <div className={styles.barActions}>
          {current && !current.active && (
            <button
              type="button"
              className={styles.setLiveButton}
              onClick={() => onSetActive(current.id)}
              title="Make this the live version that customers and orders use."
            >
              Set Live
            </button>
          )}
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDeleteClick}
            disabled={!canDelete && !canDeleteProduct}
            aria-label={canDelete ? "Delete" : "Delete this product"}
            title={
              canDelete
                ? "Delete this version or the whole product."
                : canDeleteProduct
                ? "Delete this product."
                : "Save the product before deleting it."
            }
          >
            <Bin size={24} />
          </button>
        </div>
      </div>

      {confirmingDelete && current && (
        <div className={styles.confirmBackdrop} onClick={handleCancelDelete}>
          <div
            className={styles.confirmPanel}
            onClick={(e) => e.stopPropagation()}
          >
            {canDelete ? (
              <p className={styles.confirmText}>
                Delete the version <strong>{current.name}</strong>
                {canDeleteProduct ? (
                  <>, or the whole product?</>
                ) : (
                  <>? This can&rsquo;t be undone.</>
                )}
              </p>
            ) : (
              <p className={styles.confirmText}>
                Delete <strong>{productName || "this product"}</strong>? It
                leaves the admin, and the store if it&rsquo;s live. Getting it
                back needs backend help.
              </p>
            )}
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancel}
                onClick={handleCancelDelete}
                title="Keep this version and close this dialog."
              >
                Cancel
              </button>
              {canDelete && (
                <button
                  type="button"
                  className={styles.confirmDelete}
                  onClick={handleConfirmDelete}
                  title="Permanently delete only this version. The product and its other versions stay."
                >
                  Delete Version
                </button>
              )}
              {canDeleteProduct && (
                <button
                  type="button"
                  className={styles.confirmDelete}
                  onClick={handleConfirmDeleteProduct}
                  title="Delete the whole product. It leaves the admin, and the store if it's live."
                >
                  Delete Product
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

VersionPicker.propTypes = {
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      active: PropTypes.bool,
      description: PropTypes.string,
    })
  ).isRequired,
  currentVersionId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onSetActive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  // Omit while the product isn't saved yet; there is nothing to delete.
  onDeleteProduct: PropTypes.func,
  productName: PropTypes.string,
};

export default VersionPicker;
